import React from 'react';

import type { Reservation } from '@/api/reservations/types';
import { getCarImages } from '@/core';

// Web-only implementation of the "Download PDF" action.
//
// Both `@react-pdf/renderer` and `./document` depend on browser APIs (canvas,
// document, URL.createObjectURL, etc.) and cannot be bundled into the
// iOS / Android bundle without crashing the app at startup. To keep those
// modules out of the native bundle entirely, this file ships a no-op
// counterpart at `./index.native.tsx` — Metro picks the `.native` variant on
// iOS / Android, so neither this file nor anything it imports is reachable
// from the native dependency graph.
//
// This whole folder also lives outside `src/app/` on purpose: Expo Router
// auto-imports every file under `app/` as a route, which would pull
// @react-pdf/renderer into the native bundle even with `.native.tsx`
// overrides in place.
//
// The actual web-only library imports are wrapped in dynamic `import()`
// calls so they're code-split on web too: the ~500KB PDF runtime only ships
// to the rare desktop user who clicks the button.

// @react-pdf/renderer only embeds PNG/JPEG and trusts the data-URI prefix.
// Normalize every image (remote URLs, data URIs, WebP, mislabeled signatures)
// through fetch → canvas → PNG before passing to PdfImage.
const blobToPngDataUri = async (blob: Blob): Promise<string> => {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d unavailable');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close?.();
  return canvas.toDataURL('image/png');
};

// Redirect URLs 302 to S3; cross-origin redirect + fetch fails in the browser
// ("Failed to fetch") even when S3 sends CORS headers. Proxy URLs stream the
// bytes from Rails directly.
const toActiveStorageProxyUrl = (url: string): string =>
  url.includes('/rails/active_storage/blobs/redirect/')
    ? url.replace('/blobs/redirect/', '/blobs/proxy/')
    : url;

const prepareImageForPdf = async (src: string): Promise<string | undefined> => {
  const fetchUrl = src.startsWith('data:') ? src : toActiveStorageProxyUrl(src);

  for (const credentials of ['omit', 'include'] as const) {
    try {
      const response = await fetch(fetchUrl, { credentials, mode: 'cors' });
      if (!response.ok) {
        console.warn(
          `[RentalAgreement] Image fetch returned HTTP ${response.status}`
        );
        continue;
      }
      return await blobToPngDataUri(await response.blob());
    } catch (err) {
      console.warn(
        `[RentalAgreement] Image prepare failed (credentials=${credentials})`,
        err
      );
    }
  }
  return undefined;
};

export const downloadRentalAgreementPdf = async (
  reservation: Reservation,
  onError?: (err: unknown) => void
): Promise<void> => {
  if (typeof document === 'undefined') return;
  try {
    const [{ pdf }, { RentalAgreementPdfDocument }] = await Promise.all([
      import('@react-pdf/renderer'),
      import('./document'),
    ]);

    const signatureDataUri = reservation.contractSignatureData
      ? await prepareImageForPdf(reservation.contractSignatureData)
      : undefined;

    const carImageUrl = reservation.car
      ? (['small', 'medium', 'large', 'extraLarge'] as const)
          .map(
            (size) =>
              getCarImages(reservation.car!.images || [], size)?.[0]?.url
          )
          .find(Boolean)
      : undefined;
    const carImageDataUri = carImageUrl
      ? await prepareImageForPdf(carImageUrl)
      : undefined;

    const enrichedReservation: Reservation = {
      ...reservation,
      contractSignatureData: signatureDataUri ?? '',
    };

    const blob = await pdf(
      <RentalAgreementPdfDocument
        reservation={enrichedReservation}
        carImageDataUri={carImageDataUri}
      />
    ).toBlob();

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rental-agreement-TR-${reservation.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    console.error('[RentalAgreement] Failed to generate PDF:', err);
    onError?.(err);
  }
};
